import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2, Mail, Loader2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Variant controls colour scheme:
// "dark"  — for the orange/dark CTA block (input is on a dark surface)
// "light" — for cream/orange sections (input is on a light surface)
export const WaitlistForm = ({ variant = 'dark', testIdPrefix = 'waitlist' }) => {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');
  const [already, setAlready] = useState(false);

  const copy = t.waitlist;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(copy.invalid);
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        email: trimmed,
        locale: lang,
        source: 'website',
      });
      setAlready(Boolean(data.already_subscribed));
      setStatus('success');
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (httpStatus === 429) {
        setError(copy.rateLimited);
      } else {
        setError(typeof detail === 'string' ? detail : copy.errorGeneric);
      }
      setStatus('error');
    }
  };

  const isDark = variant === 'dark';

  if (status === 'success') {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={`flex items-start gap-3 rounded-2xl px-5 py-4 ${
          isDark
            ? 'bg-white/10 text-white border border-white/15'
            : 'bg-emerald-50 text-emerald-800 border border-emerald-100'
        }`}
      >
        <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-bold text-sm">
            {already ? copy.alreadyTitle : copy.successTitle}
          </div>
          <div className={`text-sm mt-0.5 ${isDark ? 'text-white/80' : 'text-emerald-700'}`}>
            {already ? copy.alreadyBody : copy.successBody}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid={`${testIdPrefix}-form`}
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div
        className={`flex flex-col sm:flex-row items-stretch gap-2 rounded-2xl p-1.5 ${
          isDark ? 'bg-white/10 border border-white/15' : 'bg-white border border-stone-200 shadow-sm'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 px-3">
          <Mail className={`h-4 w-4 ${isDark ? 'text-white/60' : 'text-stone-400'}`} />
          <input
            type="email"
            inputMode="email"
            required
            autoComplete="email"
            placeholder={copy.placeholder}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'error') setStatus('idle');
            }}
            disabled={status === 'loading'}
            data-testid={`${testIdPrefix}-email-input`}
            className={`flex-1 bg-transparent border-0 outline-none py-3 text-sm font-medium ${
              isDark ? 'text-white placeholder:text-white/40' : 'text-stone-900 placeholder:text-stone-400'
            }`}
          />
        </div>
        <button
          type="submit"
          disabled={status === 'loading'}
          data-testid={`${testIdPrefix}-submit`}
          className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.97] disabled:opacity-70 disabled:cursor-not-allowed ${
            isDark
              ? 'bg-orange-500 text-white hover:bg-orange-400'
              : 'bg-stone-900 text-white hover:bg-stone-800'
          }`}
        >
          {status === 'loading' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> {copy.loading}
            </>
          ) : (
            <>
              {copy.button} <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
      {status === 'error' && (
        <div
          data-testid={`${testIdPrefix}-error`}
          className={`mt-2 text-xs font-semibold ${
            isDark ? 'text-orange-200' : 'text-rose-600'
          }`}
        >
          {error}
        </div>
      )}
      <div
        className={`mt-2 text-[11px] ${
          isDark ? 'text-white/50' : 'text-stone-500'
        }`}
      >
        {copy.legal}
      </div>
    </form>
  );
};
